import * as THREE from 'three';

const MAX_PARTICLES = 512;
const MAX_RINGS = 48;
const GOLDEN_ANGLE = Math.PI * (3 - Math.sqrt(5));

export class EffectsSystem {
  readonly group = new THREE.Group();

  private readonly particleActive = new Uint8Array(MAX_PARTICLES);
  private readonly particleX = new Float32Array(MAX_PARTICLES);
  private readonly particleY = new Float32Array(MAX_PARTICLES);
  private readonly particleVx = new Float32Array(MAX_PARTICLES);
  private readonly particleVy = new Float32Array(MAX_PARTICLES);
  private readonly particleLife = new Float32Array(MAX_PARTICLES);
  private readonly particleMaxLife = new Float32Array(MAX_PARTICLES);
  private readonly particleSize = new Float32Array(MAX_PARTICLES);
  private readonly particleColor = new Float32Array(MAX_PARTICLES * 3);
  private readonly particleGeometry = new THREE.OctahedronGeometry(1, 0);
  private readonly particleMaterial = new THREE.MeshBasicMaterial({
    color: '#ffffff',
    transparent: true,
    opacity: 0.92,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    toneMapped: false,
  });
  private readonly particles = new THREE.InstancedMesh(
    this.particleGeometry,
    this.particleMaterial,
    MAX_PARTICLES,
  );

  private readonly ringActive = new Uint8Array(MAX_RINGS);
  private readonly ringX = new Float32Array(MAX_RINGS);
  private readonly ringY = new Float32Array(MAX_RINGS);
  private readonly ringLife = new Float32Array(MAX_RINGS);
  private readonly ringMaxLife = new Float32Array(MAX_RINGS);
  private readonly ringRadius = new Float32Array(MAX_RINGS);
  private readonly ringGrowth = new Float32Array(MAX_RINGS);
  private readonly ringColor = new Float32Array(MAX_RINGS * 3);
  private readonly ringGeometry = new THREE.RingGeometry(0.82, 1, 28);
  private readonly ringMaterial = new THREE.MeshBasicMaterial({
    color: '#ffffff',
    transparent: true,
    opacity: 0.54,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    side: THREE.DoubleSide,
    toneMapped: false,
  });
  private readonly rings = new THREE.InstancedMesh(
    this.ringGeometry,
    this.ringMaterial,
    MAX_RINGS,
  );

  private readonly flashMaterial = new THREE.MeshBasicMaterial({
    color: '#4fe0ff',
    transparent: true,
    opacity: 0,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    toneMapped: false,
  });
  private readonly flash = new THREE.Mesh(
    new THREE.PlaneGeometry(72, 56),
    this.flashMaterial,
  );
  private readonly dummy = new THREE.Object3D();
  private readonly color = new THREE.Color();
  private flashLife = 0;
  private flashMaxLife = 0.001;

  constructor() {
    this.group.name = 'CombatEffects';
    this.particles.frustumCulled = false;
    this.particles.renderOrder = 30;
    this.rings.frustumCulled = false;
    this.rings.renderOrder = 29;
    this.flash.position.z = 3.6;
    this.flash.renderOrder = 28;
    this.flash.visible = false;
    this.group.add(this.flash, this.rings, this.particles);
  }

  /**
   * Engine exhaust.
   *
   * THIS WAS THE PURPLE AND GREEN SMOKE. The two particle colours were
   * hardcoded here as 0x65ff66 (green) and 0xb969ff (violet) — not on any
   * material, which is why searching materials for the offending colours kept
   * coming back clean while the artifact stayed on screen.
   *
   * An afterburner is white at the throat grading to deep orange. Heat is the
   * only colour permitted anywhere on this aircraft.
   */
  spawnEngineExhaust(x: number, y: number, frame: number): void {
    const side = frame % 4 < 2 ? -1 : 1;
    this.spawnParticle(
      x + side * 0.34,
      y - 1.38,
      side * 0.18,
      -4.2 - (frame % 3) * 0.35,
      0.48,
      0.12,
      0xffd9a8,
    );
    this.spawnParticle(
      x - side * 0.34,
      y - 1.38,
      -side * 0.14,
      -3.5,
      0.36,
      0.08,
      0xff6a1e,
    );
  }

  spawnImpact(x: number, y: number, destroyed: boolean): void {
    const count = destroyed ? 18 : 5;
    const color = destroyed ? 0xff6d24 : 0xffe7a3;
    const speed = destroyed ? 7.4 : 3.4;
    for (let index = 0; index < count; index += 1) {
      const angle = index * GOLDEN_ANGLE;
      const variance = 0.58 + ((index * 17) % 11) / 10;
      this.spawnParticle(
        x,
        y,
        Math.cos(angle) * speed * variance,
        Math.sin(angle) * speed * variance,
        destroyed ? 0.72 : 0.24,
        destroyed ? 0.16 : 0.075,
        // Every fourth spark runs hot white instead of the tier colour, so a
        // burst has internal variation. This was violet (0xb166ff), which put
        // purple confetti on every impact and clashed with an airframe whose
        // only colour is heat.
        index % 4 === 0 ? 0xfff0cf : color,
      );
    }
    if (destroyed) this.spawnRing(x, y, 0.28, 3.5, 0.42, 0xff6338);
  }

  spawnPlayerDamage(x: number, y: number): void {
    for (let index = 0; index < 26; index += 1) {
      const angle = index * GOLDEN_ANGLE;
      const speed = 4.5 + (index % 7) * 0.7;
      this.spawnParticle(
        x,
        y,
        Math.cos(angle) * speed,
        Math.sin(angle) * speed,
        0.82,
        0.18,
        // Player-death debris. Was green (0x65ff66) mixed into the red — the
        // last live instance of the palette this aircraft is not allowed to
        // wear. Hot white against the red reads as burning metal instead.
        index % 3 === 0 ? 0xfff0cf : 0xe92738,
      );
    }
    this.spawnRing(x, y, 0.32, 4.8, 0.56, 0xef2947);
    this.flashMaterial.color.setHex(0xb20c24);
    this.flashLife = 0.16;
    this.flashMaxLife = this.flashLife;
    this.flash.visible = true;
  }

  spawnBomb(x: number, y: number): void {
    this.spawnRing(x, y, 0.45, 35, 0.78, 0x9c6dff);
    this.spawnRing(x, y, 0.2, 22, 0.46, 0xe7d4ff);
    for (let index = 0; index < 64; index += 1) {
      const angle = index * GOLDEN_ANGLE;
      const speed = 7 + (index % 9) * 0.85;
      this.spawnParticle(
        x,
        y,
        Math.cos(angle) * speed,
        Math.sin(angle) * speed,
        0.95,
        0.22,
        // Bomb detonation. Was violet (0x9e60ff) — the player's own ordnance
        // should read as OUR ordnance: white core, hot amber body. Violet is
        // the enemy's colour family and it made a friendly bomb look hostile.
        index % 5 === 0 ? 0xffffff : 0xffb347,
      );
    }
    this.flashMaterial.color.setHex(0xd57a2c);
    this.flashLife = 0.28;
    this.flashMaxLife = this.flashLife;
    this.flash.visible = true;
  }

  update(delta: number): void {
    for (let index = 0; index < MAX_PARTICLES; index += 1) {
      if (!this.particleActive[index]) continue;
      this.particleLife[index] -= delta;
      if (this.particleLife[index] <= 0) {
        this.particleActive[index] = 0;
        continue;
      }
      this.particleX[index] += this.particleVx[index] * delta;
      this.particleY[index] += this.particleVy[index] * delta;
      this.particleVx[index] *= Math.max(0, 1 - delta * 2.4);
      this.particleVy[index] *= Math.max(0, 1 - delta * 2.4);
    }
    for (let index = 0; index < MAX_RINGS; index += 1) {
      if (!this.ringActive[index]) continue;
      this.ringLife[index] -= delta;
      if (this.ringLife[index] <= 0) this.ringActive[index] = 0;
    }
    if (this.flashLife > 0) {
      this.flashLife = Math.max(0, this.flashLife - delta);
      this.flashMaterial.opacity = (this.flashLife / this.flashMaxLife) * 0.34;
      this.flash.visible = this.flashLife > 0;
    }
    this.syncVisuals();
  }

  reset(): void {
    this.particleActive.fill(0);
    this.ringActive.fill(0);
    this.flashLife = 0;
    this.flash.visible = false;
    this.particles.count = 0;
    this.rings.count = 0;
  }

  dispose(): void {
    this.particleGeometry.dispose();
    this.particleMaterial.dispose();
    this.ringGeometry.dispose();
    this.ringMaterial.dispose();
    this.flash.geometry.dispose();
    this.flashMaterial.dispose();
  }

  private spawnParticle(
    x: number,
    y: number,
    vx: number,
    vy: number,
    life: number,
    size: number,
    color: number,
  ): void {
    for (let index = 0; index < MAX_PARTICLES; index += 1) {
      if (this.particleActive[index]) continue;
      this.particleActive[index] = 1;
      this.particleX[index] = x;
      this.particleY[index] = y;
      this.particleVx[index] = vx;
      this.particleVy[index] = vy;
      this.particleLife[index] = life;
      this.particleMaxLife[index] = life;
      this.particleSize[index] = size;
      this.color.setHex(color);
      this.particleColor[index * 3] = this.color.r;
      this.particleColor[index * 3 + 1] = this.color.g;
      this.particleColor[index * 3 + 2] = this.color.b;
      return;
    }
  }

  private spawnRing(
    x: number,
    y: number,
    radius: number,
    growth: number,
    life: number,
    color: number,
  ): void {
    for (let index = 0; index < MAX_RINGS; index += 1) {
      if (this.ringActive[index]) continue;
      this.ringActive[index] = 1;
      this.ringX[index] = x;
      this.ringY[index] = y;
      this.ringLife[index] = life;
      this.ringMaxLife[index] = life;
      this.ringRadius[index] = radius;
      this.ringGrowth[index] = growth;
      this.color.setHex(color);
      this.ringColor[index * 3] = this.color.r;
      this.ringColor[index * 3 + 1] = this.color.g;
      this.ringColor[index * 3 + 2] = this.color.b;
      return;
    }
  }

  private syncVisuals(): void {
    let particleCount = 0;
    for (let index = 0; index < MAX_PARTICLES; index += 1) {
      if (!this.particleActive[index]) continue;
      const lifeRatio = this.particleLife[index] / this.particleMaxLife[index];
      const scale = this.particleSize[index] * (0.32 + lifeRatio * 0.88);
      this.dummy.position.set(this.particleX[index], this.particleY[index], 2.2);
      this.dummy.rotation.set(0, 0, this.particleX[index] * 0.7);
      this.dummy.scale.set(scale, scale * 1.7, scale);
      this.dummy.updateMatrix();
      this.particles.setMatrixAt(particleCount, this.dummy.matrix);
      this.color.setRGB(
        this.particleColor[index * 3] * lifeRatio,
        this.particleColor[index * 3 + 1] * lifeRatio,
        this.particleColor[index * 3 + 2] * lifeRatio,
      );
      this.particles.setColorAt(particleCount, this.color);
      particleCount += 1;
    }
    this.particles.count = particleCount;
    this.particles.instanceMatrix.needsUpdate = true;
    if (this.particles.instanceColor) this.particles.instanceColor.needsUpdate = true;

    let ringCount = 0;
    for (let index = 0; index < MAX_RINGS; index += 1) {
      if (!this.ringActive[index]) continue;
      const lifeRatio = this.ringLife[index] / this.ringMaxLife[index];
      const progress = 1 - lifeRatio;
      const scale = this.ringRadius[index] + this.ringGrowth[index] * progress;
      this.dummy.position.set(this.ringX[index], this.ringY[index], 2);
      this.dummy.rotation.set(0, 0, 0);
      this.dummy.scale.setScalar(scale);
      this.dummy.updateMatrix();
      this.rings.setMatrixAt(ringCount, this.dummy.matrix);
      this.color.setRGB(
        this.ringColor[index * 3] * lifeRatio,
        this.ringColor[index * 3 + 1] * lifeRatio,
        this.ringColor[index * 3 + 2] * lifeRatio,
      );
      this.rings.setColorAt(ringCount, this.color);
      ringCount += 1;
    }
    this.rings.count = ringCount;
    this.rings.instanceMatrix.needsUpdate = true;
    if (this.rings.instanceColor) this.rings.instanceColor.needsUpdate = true;
  }
}
